// eslint-disable-next-line @typescript-eslint/triple-slash-reference -- Support direct source imports.
/// <reference path="./styles.d.ts" />
import {
  Children,
  cloneElement,
  forwardRef,
  Fragment,
  isValidElement,
  useCallback,
  useEffect,
  useId,
  useLayoutEffect,
  useRef,
  useState,
  type AnchorHTMLAttributes,
  type ButtonHTMLAttributes,
  type AriaAttributes,
  type ComponentPropsWithoutRef,
  type CSSProperties,
  type DetailsHTMLAttributes,
  type FormEvent,
  type FormHTMLAttributes,
  type HTMLAttributes,
  type InputHTMLAttributes,
  type LabelHTMLAttributes,
  type ProgressHTMLAttributes,
  type ReactElement,
  type ReactNode,
  type Ref,
  type SelectHTMLAttributes,
  type TextareaHTMLAttributes,
} from "react";
import { createPortal } from "react-dom";
import {
  Check,
  ChevronDown,
  Copy as CopyIcon,
  Search as SearchIcon,
} from "lucide-react";
import { writeClipboardText } from "./clipboard";
import { Image } from "./Image";
import { SelectionIndicator } from "./SelectionIndicator";
import { useMotionPolicy } from "./motionPolicy";
import "./Controls.css";

export type ControlSize = "small" | "middle" | "large";

function classes(...values: Array<string | false | undefined>) {
  return values.filter(Boolean).join(" ");
}

function assignRef<T>(ref: Ref<T> | undefined, value: T | null) {
  if (typeof ref === "function") ref(value);
  else if (ref) ref.current = value;
}

function CloseSymbol() {
  return (
    <svg aria-hidden="true" width="1em" height="1em" viewBox="0 0 16 16">
      <path
        d="m4 4 8 8M12 4l-8 8"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.5"
      />
    </svg>
  );
}

type LiveRegionElement = "aside" | "div" | "p" | "span";
type LiveRegionPoliteness = "assertive" | "polite";

interface LiveRegionOwnProps {
  as?: LiveRegionElement;
  atomic?: boolean;
  busy?: boolean;
  politeness?: LiveRegionPoliteness;
}

export type LiveRegionProps<Element extends LiveRegionElement = "span"> =
  LiveRegionOwnProps &
    Omit<
      ComponentPropsWithoutRef<Element>,
      | keyof LiveRegionOwnProps
      | "aria-atomic"
      | "aria-busy"
      | "aria-live"
      | "role"
    >;

export function LiveRegion<Element extends LiveRegionElement = "span">({
  as,
  atomic = true,
  busy = false,
  politeness = "polite",
  ...props
}: LiveRegionProps<Element>) {
  const Component = as ?? "span";
  return (
    <Component
      {...props}
      aria-atomic={atomic}
      aria-busy={busy || undefined}
      aria-live={politeness}
      role={
        Component === "aside"
          ? undefined
          : politeness === "assertive"
            ? "alert"
            : "status"
      }
    />
  );
}

export const visuallyHiddenClassName = "ark-visually-hidden";

export interface VisuallyHiddenProps extends HTMLAttributes<HTMLSpanElement> {
  asChild?: boolean;
}

export function VisuallyHidden({
  asChild = false,
  children,
  className,
  ...props
}: VisuallyHiddenProps) {
  const resolvedClassName = classes(visuallyHiddenClassName, className);
  if (!asChild) {
    return (
      <span {...props} className={resolvedClassName}>
        {children}
      </span>
    );
  }
  if (
    Children.count(children) !== 1 ||
    !isValidElement<{ className?: string }>(children) ||
    children.type === Fragment
  ) {
    throw new Error(
      "VisuallyHidden with asChild requires exactly one non-Fragment React element.",
    );
  }
  return cloneElement(children, {
    ...props,
    className: classes(
      visuallyHiddenClassName,
      children.props.className,
      className,
    ),
  });
}

export interface InlineFeedbackProps extends Omit<
  LiveRegionProps<"p">,
  "as" | "politeness"
> {
  as?: LiveRegionElement;
  politeness?: LiveRegionPoliteness;
  type?: "error" | "info" | "success" | "warning";
}

export function InlineFeedback({
  as = "p",
  politeness,
  type = "info",
  className,
  ...props
}: InlineFeedbackProps) {
  return (
    <LiveRegion
      {...props}
      as={as}
      className={classes("ark-control ark-inline-feedback", className)}
      data-type={type}
      politeness={politeness ?? (type === "error" ? "assertive" : "polite")}
    />
  );
}

export interface SpinnerProps extends HTMLAttributes<HTMLSpanElement> {
  size?: ControlSize | number;
  tip?: ReactNode;
}

export function Spinner({
  size = "middle",
  tip,
  className,
  style,
  ...props
}: SpinnerProps) {
  return (
    <span
      role="status"
      aria-label={tip ? undefined : "加载中"}
      {...props}
      className={classes("ark-control ark-spinner", className)}
      data-size={typeof size === "string" ? size : undefined}
      style={{
        ...style,
        ...(typeof size === "number"
          ? { "--ark-spinner-size": `${size}px` }
          : {}),
      }}
    >
      <span aria-hidden="true" className="ark-spinner-ring" />
      {tip ? <span>{tip}</span> : null}
    </span>
  );
}

export interface StatisticProps extends Omit<
  HTMLAttributes<HTMLDListElement>,
  "children"
> {
  icon?: ReactNode;
  label: ReactNode;
  labelPosition?: "after" | "before";
  loading?: boolean;
  loadingValue?: ReactNode;
  supporting?: ReactNode;
  value: ReactNode;
}

export const Statistic = forwardRef<HTMLDListElement, StatisticProps>(
  function Statistic(
    {
      className,
      icon,
      label,
      labelPosition = "before",
      loading = false,
      loadingValue = "—",
      supporting,
      value,
      ...props
    },
    ref,
  ) {
    return (
      <dl
        {...props}
        ref={ref}
        aria-busy={loading || undefined}
        className={classes("ark-control ark-statistic", className)}
        data-label-position={labelPosition}
        data-loading={loading || undefined}
      >
        <dt className="ark-statistic-label">
          {icon ? (
            <span aria-hidden="true" className="ark-statistic-icon">
              {icon}
            </span>
          ) : null}
          {label}
        </dt>
        <dd className="ark-statistic-value">
          {loading ? loadingValue : value}
        </dd>
        {supporting !== undefined && supporting !== null ? (
          <dd className="ark-statistic-supporting">{supporting}</dd>
        ) : null}
      </dl>
    );
  },
);

export interface ButtonProps extends Omit<
  ButtonHTMLAttributes<HTMLButtonElement>,
  "type"
> {
  type?: "primary" | "default" | "text" | "link" | "unstyled";
  htmlType?: ButtonHTMLAttributes<HTMLButtonElement>["type"];
  danger?: boolean;
  loading?: boolean;
  icon?: ReactNode;
  size?: ControlSize;
  block?: boolean;
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  function Button(
    {
      type = "default",
      htmlType = "button",
      danger = false,
      loading = false,
      icon,
      size = "middle",
      block = false,
      disabled,
      children,
      className,
      onClick,
      ...props
    },
    ref,
  ) {
    const unstyled = type === "unstyled";
    return (
      <button
        {...props}
        ref={ref}
        type={htmlType}
        disabled={disabled || loading}
        aria-busy={loading || undefined}
        className={classes(
          "ark-control",
          unstyled ? "ark-button-unstyled" : "ark-button",
          block && "ark-button-block",
          !unstyled &&
            Boolean(icon) &&
            (children === undefined || children === null) &&
            !block &&
            "ark-button-icon-only",
          className,
        )}
        data-variant={unstyled ? undefined : type}
        data-danger={danger || undefined}
        data-size={size}
        onClick={(event) => {
          if (!disabled && !loading) onClick?.(event);
        }}
      >
        {loading ? (
          <span aria-hidden="true">
            <Spinner size="small" />
          </span>
        ) : icon ? (
          <span className="ark-button-icon">{icon}</span>
        ) : null}
        {children}
      </button>
    );
  },
);

export interface SegmentedOption<Value extends string> {
  className?: string;
  disabled?: boolean;
  icon?: ReactNode;
  label: ReactNode;
  value: Value;
}

export type SegmentedProps<Value extends string> = Omit<
  HTMLAttributes<HTMLDivElement>,
  "aria-label" | "aria-labelledby" | "onChange"
> &
  AccessibleName & {
    activeType?: ButtonProps["type"];
    disabled?: boolean;
    inactiveType?: ButtonProps["type"];
    onChange: (value: Value) => void;
    optionClassName?: (
      option: SegmentedOption<Value>,
      active: boolean,
    ) => string | undefined;
    options: ReadonlyArray<SegmentedOption<Value>>;
    size?: ControlSize;
    value: Value;
  };

export function Segmented<Value extends string>({
  activeType = "primary",
  className,
  disabled = false,
  inactiveType = "text",
  onChange,
  onKeyDown,
  optionClassName,
  options,
  size = "middle",
  value,
  ...props
}: SegmentedProps<Value>) {
  const root = useRef<HTMLDivElement>(null);
  return (
    <div
      {...props}
      ref={root}
      className={classes("ark-control ark-segmented", className)}
      onKeyDown={(event) => {
        onKeyDown?.(event);
        if (event.defaultPrevented) return;
        const buttons = Array.from(
          event.currentTarget.querySelectorAll<HTMLButtonElement>(
            "[data-ark-segmented-option]:not(:disabled)",
          ),
        );
        const index = buttons.findIndex(
          (button) => button === document.activeElement,
        );
        if (index < 0) return;
        let next: number | undefined;
        if (event.key === "ArrowRight" || event.key === "ArrowDown") {
          next = (index + 1) % buttons.length;
        } else if (event.key === "ArrowLeft" || event.key === "ArrowUp") {
          next = (index - 1 + buttons.length) % buttons.length;
        } else if (event.key === "Home") {
          next = 0;
        } else if (event.key === "End") {
          next = buttons.length - 1;
        }
        if (next === undefined) return;
        event.preventDefault();
        buttons[next]?.focus();
      }}
      role="group"
    >
      <SelectionIndicator rootRef={root} />
      {options.map((option) => {
        const active = option.value === value;
        return (
          <Button
            aria-pressed={active}
            className={classes(
              option.className,
              optionClassName?.(option, active),
            )}
            data-active={active || undefined}
            data-ark-segmented-option=""
            data-ark-selection-item=""
            disabled={disabled || option.disabled}
            icon={option.icon}
            key={option.value}
            onClick={() => onChange(option.value)}
            size={size}
            type={active ? activeType : inactiveType}
          >
            {option.label}
          </Button>
        );
      })}
    </div>
  );
}

export interface CopyButtonProps
  extends Omit<ButtonProps, "loading" | "onClick"> {
  value: string;
  onCopied?: (value: string) => void;
  onCopyError?: (error: unknown) => void;
}

export function CopyButton({
  value,
  onCopied,
  onCopyError,
  children = "复制",
  icon = <CopyIcon />,
  size = "small",
  type = "text",
  ...props
}: CopyButtonProps) {
  const pending = useRef(false);
  const [copying, setCopying] = useState(false);
  return (
    <Button
      {...props}
      icon={icon}
      loading={copying}
      size={size}
      type={type}
      onClick={() => {
        if (pending.current) return;
        pending.current = true;
        setCopying(true);
        void (async () => {
          let copied = false;
          let failure: unknown;
          try {
            await writeClipboardText(value);
            copied = true;
          } catch (cause) {
            failure = cause;
          } finally {
            pending.current = false;
            setCopying(false);
          }
          if (copied) onCopied?.(value);
          else onCopyError?.(failure);
        })();
      }}
    >
      {children}
    </Button>
  );
}

export interface CopyableValueProps
  extends Omit<HTMLAttributes<HTMLSpanElement>, "children"> {
  buttonChildren?: ReactNode;
  children?: ReactNode;
  display?: "code" | "text";
  label: string;
  onCopied?: CopyButtonProps["onCopied"];
  onCopyError?: CopyButtonProps["onCopyError"];
  overflow?: "truncate" | "wrap";
  tooltip?: ReactNode;
  value: string;
}

export function CopyableValue({
  buttonChildren = null,
  children,
  className,
  display = "code",
  label,
  onCopied,
  onCopyError,
  overflow = "wrap",
  tooltip = "复制",
  value,
  ...props
}: CopyableValueProps) {
  const ValueElement = display === "code" ? "code" : "span";
  return (
    <span
      {...props}
      className={classes("ark-control ark-copyable-value", className)}
      data-overflow={overflow}
    >
      <ValueElement className="ark-copyable-value-content" title={value}>
        {children ?? value}
      </ValueElement>
      <Tooltip title={tooltip}>
        <CopyButton
          aria-label={`复制 ${label}`}
          value={value}
          {...(onCopied ? { onCopied } : {})}
          {...(onCopyError ? { onCopyError } : {})}
        >
          {buttonChildren}
        </CopyButton>
      </Tooltip>
    </span>
  );
}

export interface LinkProps extends AnchorHTMLAttributes<HTMLAnchorElement> {
  disabled?: boolean;
  external?: boolean;
}

function secureLinkRel(
  rel: AnchorHTMLAttributes<HTMLAnchorElement>["rel"],
  target: AnchorHTMLAttributes<HTMLAnchorElement>["target"],
) {
  const tokens = new Set(
    rel
      ?.split(/\s+/)
      .filter(Boolean)
      .filter((token) => token.toLowerCase() !== "opener") ?? [],
  );
  if (target?.toLowerCase() === "_blank") {
    tokens.add("noopener");
    tokens.add("noreferrer");
  }
  return [...tokens].join(" ") || undefined;
}

export const Link = forwardRef<HTMLAnchorElement, LinkProps>(function Link(
  {
    disabled = false,
    external = false,
    href,
    target,
    rel,
    tabIndex,
    className,
    onClick,
    ...props
  },
  ref,
) {
  const resolvedTarget = external ? (target ?? "_blank") : target;
  return (
    <a
      {...props}
      ref={ref}
      href={disabled ? undefined : href}
      target={resolvedTarget}
      rel={secureLinkRel(rel, resolvedTarget)}
      tabIndex={disabled ? -1 : tabIndex}
      aria-disabled={disabled || props["aria-disabled"] || undefined}
      className={classes("ark-link", className)}
      onClick={(event) => {
        if (disabled) {
          event.preventDefault();
          event.stopPropagation();
          return;
        }
        onClick?.(event);
      }}
    />
  );
});

export interface BreadcrumbItem {
  key: string | number;
  label: ReactNode;
  disabled?: boolean;
  href?: string;
  onClick?: () => void;
}

export interface BreadcrumbProps extends Omit<
  ComponentPropsWithoutRef<"nav">,
  "children"
> {
  items: ReadonlyArray<BreadcrumbItem>;
  listProps?: Omit<ComponentPropsWithoutRef<"ol">, "children">;
  separator?: ReactNode;
}

export const Breadcrumb = forwardRef<HTMLElement, BreadcrumbProps>(
  function Breadcrumb(
    {
      items,
      listProps,
      separator = "/",
      className,
      "aria-label": ariaLabel = "面包屑",
      ...props
    },
    ref,
  ) {
    const { className: listClassName, ...resolvedListProps } = listProps ?? {};
    return (
      <nav
        {...props}
        ref={ref}
        aria-label={ariaLabel}
        className={classes("ark-control ark-breadcrumb", className)}
      >
        <ol
          {...resolvedListProps}
          className={classes("ark-breadcrumb-list", listClassName)}
        >
          {items.map((item, index) => {
            const current = index === items.length - 1;
            const content =
              item.href !== undefined ? (
                <Link
                  aria-current={current ? "page" : undefined}
                  className="ark-breadcrumb-link"
                  href={item.href}
                  {...(item.disabled ? { disabled: true } : {})}
                  {...(item.onClick ? { onClick: item.onClick } : {})}
                >
                  {item.label}
                </Link>
              ) : item.onClick ? (
                <Button
                  aria-current={current ? "page" : undefined}
                  className="ark-breadcrumb-link"
                  type="unstyled"
                  {...(item.disabled ? { disabled: true } : {})}
                  onClick={item.onClick}
                >
                  {item.label}
                </Button>
              ) : (
                <span
                  aria-current={current ? "page" : undefined}
                  className="ark-breadcrumb-label"
                >
                  {item.label}
                </span>
              );
            return (
              <li className="ark-breadcrumb-item" key={item.key}>
                {index > 0 ? (
                  <span aria-hidden="true" className="ark-breadcrumb-separator">
                    {separator}
                  </span>
                ) : null}
                {content}
              </li>
            );
          })}
        </ol>
      </nav>
    );
  },
);

export interface PageNavigationOption<Value extends string> {
  value: Value;
  label: ReactNode;
  className?: string;
  disabled?: boolean;
  href?: string;
  icon?: ReactNode;
}

export type PageNavigationProps<Value extends string> = Omit<
  ComponentPropsWithoutRef<"nav">,
  "aria-label" | "aria-labelledby" | "children" | "onChange"
> &
  AccessibleName & {
    buttonType?: ButtonProps["type"];
    itemClassName?:
      | string
      | ((
          option: PageNavigationOption<Value>,
          active: boolean,
        ) => string | undefined);
    onChange?: (
      value: Value,
      option: PageNavigationOption<Value>,
    ) => void;
    options: ReadonlyArray<PageNavigationOption<Value>>;
    value: Value;
  };

export function PageNavigation<Value extends string>({
  buttonType = "unstyled",
  className,
  itemClassName,
  onChange,
  options,
  value,
  ...props
}: PageNavigationProps<Value>) {
  const root = useRef<HTMLElement>(null);
  return (
    <nav
      {...props}
      ref={root}
      className={classes("ark-control ark-page-navigation", className)}
    >
      <SelectionIndicator
        rootRef={root}
        variant={
          typeof itemClassName === "string" &&
          itemClassName.split(/\s+/).includes("ark-tab")
            ? "underline"
            : "surface"
        }
      />
      {options.map((option) => {
        const active = option.value === value;
        const optionClassName = classes(
          "ark-page-navigation-item",
          option.className,
          typeof itemClassName === "function"
            ? itemClassName(option, active)
            : itemClassName,
        );
        const content = (
          <>
            {option.icon ? (
              <span aria-hidden="true" className="ark-page-navigation-icon">
                {option.icon}
              </span>
            ) : null}
            {option.label}
          </>
        );
        if (option.href !== undefined) {
          return (
            <Link
              aria-current={active ? "page" : undefined}
              className={classes("ark-control", optionClassName)}
              data-active={active || undefined}
              data-ark-selection-item=""
              href={option.href}
              key={option.value}
              {...(option.disabled ? { disabled: true } : {})}
              {...(onChange
                ? {
                    onClick: (event) => {
                      if (
                        event.defaultPrevented ||
                        event.button !== 0 ||
                        event.metaKey ||
                        event.ctrlKey ||
                        event.shiftKey ||
                        event.altKey
                      ) {
                        return;
                      }
                      event.preventDefault();
                      onChange(option.value, option);
                    },
                  }
                : {})}
            >
              {content}
            </Link>
          );
        }
        if (onChange) {
          return (
            <Button
              aria-current={active ? "page" : undefined}
              className={optionClassName}
              data-active={active || undefined}
              data-ark-selection-item=""
              key={option.value}
              type={buttonType}
              {...(option.disabled ? { disabled: true } : {})}
              onClick={() => onChange(option.value, option)}
            >
              {content}
            </Button>
          );
        }
        return (
          <span
            aria-current={active ? "page" : undefined}
            className={optionClassName}
            data-active={active || undefined}
            data-ark-selection-item=""
            key={option.value}
          >
            {content}
          </span>
        );
      })}
    </nav>
  );
}

export type LabelProps = LabelHTMLAttributes<HTMLLabelElement>;

export const Label = forwardRef<HTMLLabelElement, LabelProps>(function Label(
  props,
  ref,
) {
  return <label {...props} ref={ref} />;
});

export interface InputProps extends Omit<
  InputHTMLAttributes<HTMLInputElement>,
  "size" | "prefix"
> {
  size?: ControlSize;
  prefix?: ReactNode;
  suffix?: ReactNode;
  allowClear?: boolean;
  onClear?: () => void;
}

const InputControl = forwardRef<HTMLInputElement, InputProps>(function Input(
  {
    size = "middle",
    prefix,
    suffix,
    allowClear = false,
    onClear,
    onChange,
    value,
    defaultValue,
    disabled,
    readOnly,
    className,
    style,
    ...props
  },
  ref,
) {
  const input = useRef<HTMLInputElement>(null);
  const [uncontrolledValue, setUncontrolledValue] = useState(
    defaultValue ?? "",
  );
  const currentValue = value !== undefined ? value : uncontrolledValue;
  const clearable =
    allowClear && !disabled && !readOnly && String(currentValue).length > 0;
  return (
    <span
      className={classes("ark-control ark-input-frame", className)}
      data-size={size}
      data-disabled={disabled || undefined}
      style={style}
    >
      {prefix ? <span className="ark-input-adornment">{prefix}</span> : null}
      <input
        {...props}
        className="ark-input"
        data-autofocus={props.autoFocus || undefined}
        disabled={disabled}
        readOnly={readOnly}
        value={value}
        defaultValue={defaultValue}
        ref={(element) => {
          input.current = element;
          assignRef(ref, element);
        }}
        onChange={(event) => {
          if (disabled || readOnly) return;
          setUncontrolledValue(event.currentTarget.value);
          onChange?.(event);
        }}
      />
      {clearable ? (
        <button
          aria-label="清空输入"
          className="ark-control ark-input-action"
          type="button"
          onMouseDown={(event) => event.preventDefault()}
          onClick={() => {
            const element = input.current;
            if (!element || disabled || readOnly) return;
            // Dispatch through React's native input path, preserving the event contract.
            const descriptor = Object.getOwnPropertyDescriptor(
              HTMLInputElement.prototype,
              "value",
            );
            descriptor?.set?.call(element, "");
            element.dispatchEvent(new Event("input", { bubbles: true }));
            setUncontrolledValue("");
            onClear?.();
            element.focus();
          }}
        >
          <CloseSymbol />
        </button>
      ) : null}
      {suffix ? <span className="ark-input-adornment">{suffix}</span> : null}
    </span>
  );
});

export interface TextAreaProps extends TextareaHTMLAttributes<HTMLTextAreaElement> {
  size?: ControlSize;
  variant?: "outlined" | "borderless";
}

export const TextArea = forwardRef<HTMLTextAreaElement, TextAreaProps>(
  function TextArea(
    {
      className,
      size = "middle",
      variant = "outlined",
      disabled,
      readOnly,
      onChange,
      ...props
    },
    ref,
  ) {
    return (
      <textarea
        {...props}
        ref={ref}
        disabled={disabled}
        readOnly={readOnly}
        className={classes("ark-control ark-textarea", className)}
        data-autofocus={props.autoFocus || undefined}
        data-size={size}
        data-variant={variant}
        onChange={(event) => {
          if (!disabled && !readOnly) onChange?.(event);
        }}
      />
    );
  },
);

export const Password = forwardRef<HTMLInputElement, Omit<InputProps, "type">>(
  function Password({ suffix, disabled, ...props }, ref) {
    const [visible, setVisible] = useState(false);
    return (
      <InputControl
        {...props}
        ref={ref}
        disabled={disabled}
        type={visible ? "text" : "password"}
        suffix={
          <>
            {suffix}
            <button
              type="button"
              className="ark-control ark-input-action"
              disabled={disabled}
              aria-label={visible ? "隐藏密码" : "显示密码"}
              aria-pressed={visible}
              onMouseDown={(event) => event.preventDefault()}
              onClick={() => setVisible((previous) => !previous)}
            >
              {visible ? "隐藏" : "显示"}
            </button>
          </>
        }
      />
    );
  },
);

// eslint-disable-next-line react-refresh/only-export-components -- Native input variants share one public component API.
export const Input = Object.assign(InputControl, { TextArea, Password });

type NativeTypedInputProps = Omit<
  InputHTMLAttributes<HTMLInputElement>,
  "type"
>;

function nativeTypedInput(
  type: "color" | "range",
  {
    disabled,
    readOnly,
    onChange,
    ...props
  }: NativeTypedInputProps,
  ref: Ref<HTMLInputElement>,
) {
  return (
    <input
      {...props}
      ref={ref}
      disabled={disabled}
      readOnly={readOnly}
      type={type}
      onChange={(event) => {
        if (!disabled && !readOnly) onChange?.(event);
      }}
    />
  );
}

export type RangeInputProps = NativeTypedInputProps;

export const RangeInput = forwardRef<HTMLInputElement, RangeInputProps>(
  function RangeInput(props, ref) {
    return nativeTypedInput("range", props, ref);
  },
);

export type ColorInputProps = NativeTypedInputProps;

export const ColorInput = forwardRef<HTMLInputElement, ColorInputProps>(
  function ColorInput(props, ref) {
    return nativeTypedInput("color", props, ref);
  },
);

export interface DataListOption {
  value: string | number;
  label?: string;
  disabled?: boolean;
}

export interface DataListProps extends Omit<
  ComponentPropsWithoutRef<"datalist">,
  "children"
> {
  options: readonly (string | number | DataListOption)[];
}

export const DataList = forwardRef<HTMLDataListElement, DataListProps>(
  function DataList({ options, ...props }, ref) {
    return (
      <datalist {...props} ref={ref}>
        {options.map((option, index) => {
          const resolved =
            typeof option === "object" ? option : { value: option };
          return (
            <option
              disabled={resolved.disabled}
              key={`${String(resolved.value)}:${index}`}
              label={resolved.label}
              value={resolved.value}
            />
          );
        })}
      </datalist>
    );
  },
);

type AccessibleName =
  | {
      "aria-label": string;
      "aria-labelledby"?: string;
    }
  | {
      "aria-label"?: string;
      "aria-labelledby": string;
    };

export type CodeBlockProps = Omit<
  ComponentPropsWithoutRef<"pre">,
  "aria-label" | "aria-labelledby"
> &
  AccessibleName;

export const CodeBlock = forwardRef<HTMLPreElement, CodeBlockProps>(
  function CodeBlock({ tabIndex = 0, ...props }, ref) {
    return <pre {...props} ref={ref} tabIndex={tabIndex} />;
  },
);

export interface CopyableCodeBlockProps
  extends Omit<HTMLAttributes<HTMLDivElement>, "children"> {
  content: string;
  label: string;
  maxHeight?: CSSProperties["maxHeight"];
  onCopied?: CopyButtonProps["onCopied"];
  onCopyError?: CopyButtonProps["onCopyError"];
  wrap?: boolean;
}

export function CopyableCodeBlock({
  className,
  content,
  label,
  maxHeight,
  onCopied,
  onCopyError,
  style,
  wrap = false,
  ...props
}: CopyableCodeBlockProps) {
  const resolvedMaxHeight =
    typeof maxHeight === "number" ? `${maxHeight}px` : maxHeight;
  return (
    <div
      {...props}
      className={classes("ark-control ark-copyable-code-block", className)}
      style={{
        ...style,
        ...(resolvedMaxHeight === undefined
          ? {}
          : {
              "--ark-copyable-code-block-max-height": resolvedMaxHeight,
            }),
      }}
    >
      <div className="ark-copyable-code-block-toolbar">
        <code>{label}</code>
        <Tooltip title="复制">
          <CopyButton
            aria-label={`复制 ${label}`}
            value={content}
            {...(onCopied ? { onCopied } : {})}
            {...(onCopyError ? { onCopyError } : {})}
          >
            {null}
          </CopyButton>
        </Tooltip>
      </div>
      <CodeBlock
        aria-label={label}
        className="ark-copyable-code-block-content"
        data-wrap={wrap || undefined}
      >
        {content}
      </CodeBlock>
    </div>
  );
}

export type EmbeddedFrameProps = Omit<
  ComponentPropsWithoutRef<"iframe">,
  "sandbox" | "title"
> & {
  sandbox: string;
  title: string;
};

export const EmbeddedFrame = forwardRef<
  HTMLIFrameElement,
  EmbeddedFrameProps
>(function EmbeddedFrame(
  { referrerPolicy = "no-referrer", sandbox, title, ...props },
  ref,
) {
  return (
    <iframe
      {...props}
      ref={ref}
      referrerPolicy={referrerPolicy}
      sandbox={sandbox}
      title={title}
    />
  );
});

export interface MediaSource
  extends Omit<ComponentPropsWithoutRef<"source">, "children"> {
  src: string;
}

export interface MediaTrack
  extends Omit<ComponentPropsWithoutRef<"track">, "children"> {
  src: string;
}

interface MediaResources {
  fallback?: ReactNode | undefined;
  sources?: readonly MediaSource[] | undefined;
  tracks?: readonly MediaTrack[] | undefined;
}

type AccessibleMedia = {
  controls?: boolean;
  decorative?: false;
} & AccessibleName;

interface DecorativeMedia {
  "aria-label"?: never;
  "aria-labelledby"?: never;
  controls?: false;
  decorative: true;
}

type MediaAccessibility = AccessibleMedia | DecorativeMedia;

function MediaChildren({
  fallback,
  sources,
  tracks,
}: MediaResources) {
  return (
    <>
      {sources?.map(({ src, ...source }, index) => (
        <source {...source} key={`${src}:${index}`} src={src} />
      ))}
      {tracks?.map(({ src, ...track }, index) => (
        <track {...track} key={`${src}:${index}`} src={src} />
      ))}
      {fallback}
    </>
  );
}

type NativeVideoProps = Omit<
  ComponentPropsWithoutRef<"video">,
  "aria-hidden" | "aria-label" | "aria-labelledby" | "children" | "controls"
>;

export type VideoProps = NativeVideoProps &
  MediaResources &
  MediaAccessibility;

export const Video = forwardRef<HTMLVideoElement, VideoProps>(function Video(
  {
    "aria-label": ariaLabel,
    "aria-labelledby": ariaLabelledBy,
    controls,
    decorative = false,
    fallback,
    preload = "metadata",
    sources,
    tabIndex,
    tracks,
    ...props
  },
  ref,
) {
  return (
    <video
      {...props}
      ref={ref}
      aria-hidden={decorative || undefined}
      aria-label={decorative ? undefined : ariaLabel}
      aria-labelledby={decorative ? undefined : ariaLabelledBy}
      controls={controls}
      preload={preload}
      tabIndex={decorative ? -1 : tabIndex}
    >
      <MediaChildren fallback={fallback} sources={sources} tracks={tracks} />
    </video>
  );
});

type NativeAudioProps = Omit<
  ComponentPropsWithoutRef<"audio">,
  "aria-hidden" | "aria-label" | "aria-labelledby" | "children" | "controls"
>;

export type AudioProps = NativeAudioProps &
  MediaResources &
  MediaAccessibility;

export const Audio = forwardRef<HTMLAudioElement, AudioProps>(function Audio(
  {
    "aria-label": ariaLabel,
    "aria-labelledby": ariaLabelledBy,
    controls,
    decorative = false,
    fallback,
    preload = "metadata",
    sources,
    tabIndex,
    tracks,
    ...props
  },
  ref,
) {
  return (
    <audio
      {...props}
      ref={ref}
      aria-hidden={decorative || undefined}
      aria-label={decorative ? undefined : ariaLabel}
      aria-labelledby={decorative ? undefined : ariaLabelledBy}
      controls={controls}
      preload={preload}
      tabIndex={decorative ? -1 : tabIndex}
    >
      <MediaChildren fallback={fallback} sources={sources} tracks={tracks} />
    </audio>
  );
});

export interface FilePreviewProps extends Omit<
  HTMLAttributes<HTMLDivElement>,
  "children"
> {
  documentSandbox?: string;
  mediaType: string;
  name: string;
  src: string;
  unsupportedDescription?: ReactNode;
  videoAutoPlay?: boolean;
}

type FilePreviewKind =
  | "audio"
  | "document"
  | "image"
  | "unsupported"
  | "video";

function filePreviewKind(mediaType: string): FilePreviewKind {
  const normalized = mediaType.split(";", 1)[0]?.trim().toLowerCase() ?? "";
  if (normalized.startsWith("image/")) return "image";
  if (normalized.startsWith("video/")) return "video";
  if (normalized.startsWith("audio/")) return "audio";
  if (
    normalized === "application/pdf" ||
    normalized.startsWith("text/") ||
    normalized.includes("markdown")
  ) {
    return "document";
  }
  return "unsupported";
}

export const FilePreview = forwardRef<HTMLDivElement, FilePreviewProps>(
  function FilePreview(
    {
      className,
      documentSandbox = "allow-same-origin",
      mediaType,
      name,
      src,
      unsupportedDescription = "该类型暂不支持在线预览",
      videoAutoPlay = false,
      ...props
    },
    ref,
  ) {
    const kind = filePreviewKind(mediaType);
    let content: ReactNode;
    if (kind === "image") {
      content = <Image alt={name} src={src} />;
    } else if (kind === "video") {
      content = (
        <Video aria-label={name} autoPlay={videoAutoPlay} controls src={src} />
      );
    } else if (kind === "audio") {
      content = <Audio aria-label={name} controls src={src} />;
    } else if (kind === "document") {
      content = (
        <EmbeddedFrame
          sandbox={documentSandbox}
          src={src}
          title={name}
        />
      );
    } else {
      content = <EmptyState description={unsupportedDescription} />;
    }
    return (
      <div
        {...props}
        ref={ref}
        className={classes("ark-control ark-file-preview", className)}
        data-media-kind={kind}
      >
        {content}
      </div>
    );
  },
);

export interface FileInputProps extends Omit<
  InputHTMLAttributes<HTMLInputElement>,
  "size" | "type"
> {
  size?: ControlSize;
  loading?: boolean;
  directory?: boolean;
}

export const FileInput = forwardRef<HTMLInputElement, FileInputProps>(
  function FileInput(
    {
      size = "middle",
      loading = false,
      directory = false,
      disabled,
      className,
      ...props
    },
    ref,
  ) {
    return (
      <input
        {...props}
        {...(directory ? { webkitdirectory: "", directory: "" } : {})}
        ref={ref}
        type="file"
        disabled={disabled || loading}
        aria-busy={loading || props["aria-busy"] || undefined}
        className={classes("ark-control ark-file-input", className)}
        data-size={size}
      />
    );
  },
);

export interface FileDropzoneSelection {
  source: "drop" | "input";
  dataTransfer?: DataTransfer | undefined;
}

export interface FileDropzoneState {
  dragging: boolean;
}

export interface FileDropzoneProps extends Omit<
  HTMLAttributes<HTMLDivElement>,
  "children" | "onDragEnter" | "onDragLeave" | "onDragOver" | "onDrop"
> {
  label: string;
  children: ReactNode | ((state: FileDropzoneState) => ReactNode);
  accept?: string | undefined;
  multiple?: boolean | undefined;
  directory?: boolean | undefined;
  disabled?: boolean | undefined;
  activateOnClick?: boolean | undefined;
  dropEffect?: DataTransfer["dropEffect"] | undefined;
  inputRef?: Ref<HTMLInputElement> | undefined;
  onFiles: (files: File[], selection: FileDropzoneSelection) => void;
  isFileDrag?: ((dataTransfer: DataTransfer) => boolean) | undefined;
}

function defaultIsFileDrag(dataTransfer: DataTransfer) {
  return Array.from(dataTransfer.types).includes("Files");
}

export const FileDropzone = forwardRef<HTMLDivElement, FileDropzoneProps>(
  function FileDropzone(
    {
      label,
      children,
      accept,
      multiple = false,
      directory = false,
      disabled = false,
      activateOnClick = true,
      dropEffect = disabled ? "none" : "copy",
      inputRef,
      onFiles,
      isFileDrag = defaultIsFileDrag,
      className,
      tabIndex,
      onClick,
      onKeyDown,
      ...props
    },
    ref,
  ) {
    const input = useRef<HTMLInputElement>(null);
    const dragDepth = useRef(0);
    const [dragging, setDragging] = useState(false);
    const interactive = activateOnClick && !disabled;
    const resetDrag = useCallback(() => {
      dragDepth.current = 0;
      setDragging(false);
    }, []);
    const open = useCallback(() => {
      if (interactive) input.current?.click();
    }, [interactive]);

    useEffect(() => {
      if (!dragging) return;
      window.addEventListener("blur", resetDrag);
      window.addEventListener("dragend", resetDrag);
      window.addEventListener("drop", resetDrag);
      return () => {
        window.removeEventListener("blur", resetDrag);
        window.removeEventListener("dragend", resetDrag);
        window.removeEventListener("drop", resetDrag);
      };
    }, [dragging, resetDrag]);

    return (
      <div
        {...props}
        ref={ref}
        role={activateOnClick ? "button" : "region"}
        aria-label={label}
        aria-disabled={disabled || undefined}
        className={classes("ark-control ark-file-dropzone", className)}
        data-disabled={disabled || undefined}
        data-dragging={dragging || undefined}
        data-interactive={activateOnClick || undefined}
        tabIndex={
          activateOnClick ? (disabled ? -1 : (tabIndex ?? 0)) : tabIndex
        }
        onClick={(event) => {
          onClick?.(event);
          if (
            event.defaultPrevented ||
            (event.target !== event.currentTarget &&
              (event.target as Element).closest(
                "button, a, input, select, textarea, [role='button']",
              ))
          ) {
            return;
          }
          open();
        }}
        onKeyDown={(event) => {
          onKeyDown?.(event);
          if (
            !event.defaultPrevented &&
            event.target === event.currentTarget &&
            (event.key === "Enter" || event.key === " ")
          ) {
            event.preventDefault();
            open();
          }
        }}
        onDragEnter={(event) => {
          if (!isFileDrag(event.dataTransfer)) return;
          event.preventDefault();
          dragDepth.current += 1;
          if (!disabled) setDragging(true);
        }}
        onDragOver={(event) => {
          if (!isFileDrag(event.dataTransfer)) return;
          event.preventDefault();
          event.dataTransfer.dropEffect = dropEffect;
        }}
        onDragLeave={(event) => {
          if (!isFileDrag(event.dataTransfer)) return;
          event.preventDefault();
          dragDepth.current = Math.max(0, dragDepth.current - 1);
          if (!dragDepth.current) setDragging(false);
        }}
        onDrop={(event) => {
          if (!isFileDrag(event.dataTransfer)) return;
          event.preventDefault();
          event.stopPropagation();
          resetDrag();
          if (disabled) return;
          onFiles(Array.from(event.dataTransfer.files), {
            source: "drop",
            dataTransfer: event.dataTransfer,
          });
        }}
      >
        {typeof children === "function" ? children({ dragging }) : children}
        {activateOnClick ? (
          <FileInput
            ref={(element) => {
              input.current = element;
              assignRef(inputRef, element);
            }}
            hidden
            tabIndex={-1}
            aria-label={label}
            accept={accept}
            multiple={multiple}
            directory={directory}
            disabled={disabled}
            onChange={(event) => {
              const files = Array.from(event.currentTarget.files ?? []);
              event.currentTarget.value = "";
              if (files.length) onFiles(files, { source: "input" });
            }}
          />
        ) : null}
      </div>
    );
  },
);

export interface ProgressProps extends Omit<
  ProgressHTMLAttributes<HTMLProgressElement>,
  "aria-label" | "children" | "max" | "value"
> {
  label: string;
  value: number;
  max?: number | undefined;
  size?: ControlSize | undefined;
  status?: "default" | "success" | "warning" | "danger" | undefined;
}

export const Progress = forwardRef<HTMLProgressElement, ProgressProps>(
  function Progress(
    {
      label,
      value,
      max = 100,
      size = "middle",
      status = "default",
      className,
      ...props
    },
    ref,
  ) {
    const safeMax = Number.isFinite(max) && max > 0 ? max : 100;
    const safeValue = Number.isFinite(value)
      ? Math.max(0, Math.min(safeMax, value))
      : 0;
    return (
      <progress
        {...props}
        ref={ref}
        aria-label={label}
        className={classes("ark-control ark-progress", className)}
        data-progress-size={size}
        data-status={status}
        max={safeMax}
        value={safeValue}
      />
    );
  },
);

export type MeterProps = ComponentPropsWithoutRef<"meter">;

export const Meter = forwardRef<HTMLMeterElement, MeterProps>(function Meter(
  { className, ...props },
  ref,
) {
  return (
    <meter
      {...props}
      ref={ref}
      className={classes("ark-control ark-meter", className)}
    />
  );
});

export type FormFinishHandler = (
  event: FormEvent<HTMLFormElement>,
) => void | Promise<void>;

type FormSubmissionProps =
  | {
      onFinish: FormFinishHandler;
      onSubmit?: never;
    }
  | {
      onFinish?: never;
      onSubmit?: FormHTMLAttributes<HTMLFormElement>["onSubmit"];
    };

export type FormProps = Omit<
  FormHTMLAttributes<HTMLFormElement>,
  "onSubmit"
> &
  FormSubmissionProps & {
    busy?: boolean;
  };

export const Form = forwardRef<HTMLFormElement, FormProps>(function Form(
  {
    busy = false,
    onFinish,
    onSubmit,
    "aria-busy": ariaBusy,
    ...props
  },
  ref,
) {
  const submitting = useRef(false);
  const [pending, setPending] = useState(false);
  const blocked = busy || pending;
  const finishSubmission = () => {
    submitting.current = false;
    setPending(false);
  };

  return (
    <form
      {...props}
      ref={ref}
      aria-busy={blocked ? true : ariaBusy}
      onSubmit={(event) => {
        if (blocked || submitting.current) {
          event.preventDefault();
          return;
        }
        if (!onFinish) {
          onSubmit?.(event);
          return;
        }

        event.preventDefault();
        submitting.current = true;
        let result: void | Promise<void>;
        try {
          result = onFinish(event);
        } catch (error) {
          submitting.current = false;
          throw error;
        }
        if (!result) {
          submitting.current = false;
          return;
        }

        setPending(true);
        void result.then(finishSubmission, finishSubmission);
      }}
    />
  );
});

export interface SearchFormProps extends Omit<
  FormHTMLAttributes<HTMLFormElement>,
  "children" | "onChange" | "onSubmit"
> {
  buttonProps?: Omit<
    ButtonProps,
    "children" | "htmlType" | "loading" | "size"
  >;
  clearable?: boolean;
  disabled?: boolean;
  inputProps?: Omit<
    InputProps,
    "allowClear" | "disabled" | "onChange" | "onClear" | "size" | "value"
  >;
  label: string;
  loading?: boolean;
  onChange: (value: string) => void;
  onClear?: () => void;
  onSearch: (value: string) => void;
  /** Search for all results when either keyboard editing or the clear button empties the query. */
  searchOnClear?: boolean;
  size?: ControlSize;
  submitLabel?: ReactNode;
  trim?: boolean;
  value: string;
}

export const SearchForm = forwardRef<HTMLFormElement, SearchFormProps>(
  function SearchForm(
    {
      buttonProps,
      className,
      clearable = true,
      disabled = false,
      inputProps,
      label,
      loading = false,
      onChange,
      onClear,
      onSearch,
      searchOnClear = true,
      size = "middle",
      submitLabel = "搜索",
      trim = true,
      value,
      ...props
    },
    ref,
  ) {
    const {
      "aria-label": inputAriaLabel,
      className: inputClassName,
      type = "search",
      ...restInputProps
    } = inputProps ?? {};
    const {
      "aria-label": buttonAriaLabel,
      className: buttonClassName,
      disabled: buttonDisabled,
      icon = <SearchIcon aria-hidden="true" />,
      ...restButtonProps
    } = buttonProps ?? {};
    return (
      <Form
        {...props}
        ref={ref}
        busy={loading}
        className={classes("ark-control ark-search-form", className)}
        role="search"
        onFinish={() => onSearch(trim ? value.trim() : value)}
      >
        <Input
          {...restInputProps}
          aria-label={inputAriaLabel ?? label}
          allowClear={clearable}
          className={classes("ark-search-form-input", inputClassName)}
          disabled={disabled}
          onChange={(event) => {
            const nextValue = event.currentTarget.value;
            onChange(nextValue);
            if (searchOnClear && value !== "" && nextValue === "") {
              onSearch("");
            }
          }}
          {...(onClear ? { onClear } : {})}
          size={size}
          type={type}
          value={value}
        />
        <Button
          {...restButtonProps}
          aria-label={buttonAriaLabel}
          className={classes("ark-search-form-submit", buttonClassName)}
          disabled={disabled || buttonDisabled}
          htmlType="submit"
          icon={icon}
          loading={loading}
          size={size}
        >
          {submitLabel}
        </Button>
      </Form>
    );
  },
);

export interface FieldGroupProps extends Omit<
  ComponentPropsWithoutRef<"fieldset">,
  "children"
> {
  legend?: ReactNode;
  legendProps?: Omit<ComponentPropsWithoutRef<"legend">, "children">;
  children: ReactNode;
}

export const FieldGroup = forwardRef<HTMLFieldSetElement, FieldGroupProps>(
  function FieldGroup(
    { legend, legendProps, children, className, ...props },
    ref,
  ) {
    const { className: legendClassName, ...resolvedLegendProps } =
      legendProps ?? {};
    return (
      <fieldset
        {...props}
        ref={ref}
        className={classes("ark-field-group", className)}
      >
        {legend !== undefined && legend !== null ? (
          <legend
            {...resolvedLegendProps}
            className={classes("ark-field-group-legend", legendClassName)}
          >
            {legend}
          </legend>
        ) : null}
        {children}
      </fieldset>
    );
  },
);

export interface DisclosureProps extends Omit<
  DetailsHTMLAttributes<HTMLDetailsElement>,
  "children" | "open"
> {
  summary: ReactNode;
  summaryProps?: Omit<ComponentPropsWithoutRef<"summary">, "children">;
  open?: boolean | undefined;
  defaultOpen?: boolean | undefined;
  onOpenChange?: ((open: boolean) => void) | undefined;
  children: ReactNode;
}

export const Disclosure = forwardRef<HTMLDetailsElement, DisclosureProps>(
  function Disclosure(
    {
      summary,
      summaryProps,
      open,
      defaultOpen = false,
      onOpenChange,
      onToggle,
      className,
      children,
      ...props
    },
    ref,
  ) {
    const [uncontrolledOpen, setUncontrolledOpen] = useState(defaultOpen);
    const controlled = open !== undefined;
    const renderedOpen = controlled ? open : uncontrolledOpen;
    const elementRef = useRef<HTMLDetailsElement>(null);
    const controlledOpenRef = useRef(open);
    const knownOpenRef = useRef(renderedOpen);
    useLayoutEffect(() => {
      controlledOpenRef.current = open;
      knownOpenRef.current = renderedOpen;
    }, [open, renderedOpen]);
    const { className: summaryClassName, ...resolvedSummaryProps } =
      summaryProps ?? {};
    return (
      <details
        {...props}
        ref={(element) => {
          elementRef.current = element;
          assignRef(ref, element);
        }}
        className={classes("ark-control ark-disclosure", className)}
        open={renderedOpen}
        onToggle={(event) => {
          const nextOpen = event.currentTarget.open;
          onToggle?.(event);
          if (nextOpen === knownOpenRef.current) return;
          knownOpenRef.current = nextOpen;
          if (!controlled) setUncontrolledOpen(nextOpen);
          onOpenChange?.(nextOpen);
          if (controlled) {
            queueMicrotask(() => {
              const element = elementRef.current;
              const expectedOpen = controlledOpenRef.current;
              if (
                element?.isConnected &&
                expectedOpen !== undefined &&
                element.open !== expectedOpen
              ) {
                knownOpenRef.current = expectedOpen;
                element.open = expectedOpen;
              }
            });
          }
        }}
      >
        <summary
          {...resolvedSummaryProps}
          className={classes("ark-disclosure-summary", summaryClassName)}
        >
          {summary}
        </summary>
        {children}
      </details>
    );
  },
);

export interface DescriptionItem {
  key?: string | number | undefined;
  term: ReactNode;
  detail: ReactNode;
  itemProps?: HTMLAttributes<HTMLDivElement> | undefined;
  termProps?: ComponentPropsWithoutRef<"dt"> | undefined;
  detailProps?: ComponentPropsWithoutRef<"dd"> | undefined;
}

export interface DescriptionListProps extends Omit<
  HTMLAttributes<HTMLDListElement>,
  "children"
> {
  items: ReadonlyArray<DescriptionItem>;
  grouped?: boolean | undefined;
}

export const DescriptionList = forwardRef<
  HTMLDListElement,
  DescriptionListProps
>(function DescriptionList(
  { items, grouped = false, className, ...props },
  ref,
) {
  return (
    <dl
      {...props}
      ref={ref}
      className={classes("ark-control ark-description-list", className)}
    >
      {items.map((item, index) => {
        const { className: termClassName, ...termProps } = item.termProps ?? {};
        const { className: detailClassName, ...detailProps } =
          item.detailProps ?? {};
        const content = (
          <>
            <dt
              {...termProps}
              className={classes("ark-description-list-term", termClassName)}
            >
              {item.term}
            </dt>
            <dd
              {...detailProps}
              className={classes(
                "ark-description-list-detail",
                detailClassName,
              )}
            >
              {item.detail}
            </dd>
          </>
        );
        const key = item.key ?? index;
        if (!grouped) return <Fragment key={key}>{content}</Fragment>;
        const { className: itemClassName, ...itemProps } = item.itemProps ?? {};
        return (
          <div
            {...itemProps}
            className={classes("ark-description-list-item", itemClassName)}
            key={key}
          >
            {content}
          </div>
        );
      })}
    </dl>
  );
});

export type SelectValue = string | number;
export interface SelectOption<Value extends SelectValue = string> {
  value: Value;
  label: ReactNode;
  disabled?: boolean;
  searchText?: string;
}

interface SelectBaseProps<Value extends SelectValue> extends Omit<
  SelectHTMLAttributes<HTMLSelectElement>,
  "value" | "defaultValue" | "onChange" | "size" | "multiple"
> {
  options: ReadonlyArray<SelectOption<Value>>;
  size?: ControlSize;
  placeholder?: string;
  showSearch?: boolean;
  filterOption?:
    boolean | ((query: string, option: SelectOption<Value>) => boolean);
  onSearch?: (query: string) => void;
  searchPlaceholder?: string;
  onClear?: () => void;
}

// Clearing a single selection emits undefined. Multiple selection always emits an array.
export type SelectProps<Value extends SelectValue = string> =
  SelectBaseProps<Value> &
    (
      | {
          mode?: never;
          allowClear?: false;
          value?: Value | undefined;
          defaultValue?: Value | undefined;
          onChange?: (value: Value) => void;
        }
      | {
          mode?: never;
          allowClear: true;
          value?: Value | undefined;
          defaultValue?: Value | undefined;
          onChange?: (value: Value | undefined) => void;
        }
      | {
          mode: "multiple";
          allowClear?: boolean;
          value?: Value[] | undefined;
          defaultValue?: Value[] | undefined;
          onChange?: (value: Value[]) => void;
        }
    );

function nodeText(node: ReactNode): string {
  if (typeof node === "string" || typeof node === "number") return String(node);
  if (Array.isArray(node)) return node.map(nodeText).join(" ");
  if (isValidElement<{ children?: ReactNode }>(node))
    return nodeText(node.props.children);
  return "";
}

export function Select<Value extends SelectValue = string>(
  props: SelectProps<Value>,
) {
  const {
    options,
    size = "middle",
    placeholder = "请选择",
    showSearch = false,
    filterOption = true,
    searchPlaceholder = "搜索选项",
    onSearch,
    onClear,
    className,
    style,
    disabled,
    id,
    mode,
    allowClear,
    value,
    defaultValue,
    onChange,
    name,
    autoFocus,
    form,
    required,
    tabIndex,
    title,
    ...nativeProps
  } = props;
  void onChange;
  const generatedID = useId();
  const selectID = id ?? `ark-select-${generatedID}`;
  const multiple = mode === "multiple";
  const listboxID = `${selectID}-listbox`;
  const listbox = useRef<HTMLDivElement>(null);
  const trigger = useRef<HTMLButtonElement>(null);
  const focusTarget = useRef<"first" | "last" | "selected" | "none">(
    "selected",
  );
  const showPending = useRef(false);
  const showRetry = useRef<number | undefined>(undefined);
  const suppressOpen = useRef(false);
  const [open, setOpen] = useState(false);
  const [localValue, setLocalValue] = useState<Value | Value[] | undefined>(
    defaultValue,
  );
  const [query, setQuery] = useState("");
  const controlled = Object.hasOwn(props, "value");
  const selected = controlled ? value : localValue;
  const selectedValues: Value[] =
    selected === undefined
      ? []
      : Array.isArray(selected)
        ? selected
        : [selected];
  const selectedValuesRef = useRef(selectedValues);
  useLayoutEffect(() => {
    selectedValuesRef.current =
      selected === undefined
        ? []
        : Array.isArray(selected)
          ? selected
          : [selected];
  }, [selected]);
  const indexed = options.map((option, index) => ({ option, index }));
  const matches = (option: SelectOption<Value>) =>
    !showSearch ||
    !query ||
    filterOption === false ||
    (typeof filterOption === "function"
      ? filterOption(query, option)
      : (option.searchText ?? nodeText(option.label))
          .toLocaleLowerCase()
          .includes(query.toLocaleLowerCase()));
  const visible = indexed.filter(({ option }) => matches(option));
  const validationToken = indexed.find(
    ({ option }) =>
      selectedValues.includes(option.value) && String(option.value).length > 0,
  )?.index;
  const clearable = allowClear && selectedValues.length > 0 && !disabled;
  const selectedEntry = indexed.find(({ option }) =>
    selectedValues.includes(option.value),
  );
  const selectedLabels = indexed
    .filter(({ option }) => selectedValues.includes(option.value))
    .map(({ option }) => option.label);
  const positionListbox = useCallback(() => {
    const control = trigger.current;
    const surface = listbox.current;
    if (!control || !surface || !surface.matches(":popover-open")) return;
    const margin = 8;
    const gap = 6;
    const rect = control.getBoundingClientRect();
    const viewportWidth = document.documentElement.clientWidth;
    const viewportHeight = document.documentElement.clientHeight;
    const below = viewportHeight - rect.bottom - gap - margin;
    const above = rect.top - gap - margin;
    const openAbove =
      below < Math.min(surface.scrollHeight, 240) && above > below;
    const availableHeight = Math.max(96, openAbove ? above : below);
    surface.style.maxHeight = `${availableHeight}px`;
    surface.style.minWidth = `${Math.min(rect.width, viewportWidth - margin * 2)}px`;
    const width = surface.offsetWidth;
    const height = Math.min(surface.offsetHeight, availableHeight);
    const left = Math.max(
      margin,
      Math.min(rect.left, viewportWidth - width - margin),
    );
    const top = openAbove ? rect.top - height - gap : rect.bottom + gap;
    surface.style.left = `${left}px`;
    surface.style.top = `${Math.max(
      margin,
      Math.min(top, viewportHeight - height - margin),
    )}px`;
  }, []);

  useEffect(() => {
    if (!open) return;
    window.addEventListener("resize", positionListbox);
    window.addEventListener("scroll", positionListbox, true);
    return () => {
      window.removeEventListener("resize", positionListbox);
      window.removeEventListener("scroll", positionListbox, true);
    };
  }, [open, positionListbox]);

  function optionButtons() {
    return Array.from(
      listbox.current?.querySelectorAll<HTMLButtonElement>(
        '[role="option"]:not(:disabled)',
      ) ?? [],
    );
  }

  function showListbox(
    target: "first" | "last" | "selected" | "none" = "selected",
  ) {
    if (disabled || suppressOpen.current) return;
    focusTarget.current = target;
    const surface = listbox.current;
    if (!surface || surface.matches(":popover-open") || showPending.current)
      return;
    showPending.current = true;
    try {
      surface.showPopover();
    } catch (cause) {
      showPending.current = false;
      if (
        !(cause instanceof DOMException) ||
        cause.name !== "InvalidStateError"
      )
        throw cause;
      if (showRetry.current !== undefined)
        cancelAnimationFrame(showRetry.current);
      showRetry.current = requestAnimationFrame(() => {
        showRetry.current = undefined;
        showListbox(target);
      });
      return;
    }
    queueMicrotask(() => {
      showPending.current = false;
    });
    positionListbox();
    requestAnimationFrame(positionListbox);
  }

  function hideListbox(restoreFocus: boolean) {
    showPending.current = false;
    if (showRetry.current !== undefined) {
      cancelAnimationFrame(showRetry.current);
      showRetry.current = undefined;
    }
    const surface = listbox.current;
    if (surface?.matches(":popover-open")) surface.hidePopover();
    if (restoreFocus) trigger.current?.focus();
  }

  function choose(option: SelectOption<Value>) {
    if (disabled || option.disabled) return;
    if (props.mode === "multiple") {
      const current = selectedValuesRef.current;
      const nextValue = current.includes(option.value)
        ? current.filter((item) => item !== option.value)
        : [...current, option.value];
      selectedValuesRef.current = nextValue;
      if (!controlled) setLocalValue(nextValue);
      props.onChange?.(nextValue);
      return;
    }
    if (!controlled) setLocalValue(option.value);
    props.onChange?.(option.value);
    suppressOpen.current = true;
    requestAnimationFrame(() => {
      hideListbox(true);
      requestAnimationFrame(() => {
        suppressOpen.current = false;
      });
    });
  }

  return (
    <span
      className={classes("ark-control ark-select-frame", className)}
      data-size={size}
      style={style}
    >
      {showSearch ? (
        <InputControl
          type="search"
          aria-label={`${props["aria-label"] ?? placeholder}：搜索`}
          aria-controls={listboxID}
          disabled={disabled}
          placeholder={searchPlaceholder}
          size={size}
          value={query}
          onFocus={() => showListbox("none")}
          onChange={(event) => {
            setQuery(event.currentTarget.value);
            onSearch?.(event.currentTarget.value);
            showListbox("none");
          }}
          onKeyDown={(event) => {
            if (event.key === "ArrowDown") {
              event.preventDefault();
              showListbox("first");
            }
            if (event.key === "Escape") {
              setQuery("");
              onSearch?.("");
            }
          }}
        />
      ) : null}
      <span className="ark-select-row">
        <button
          {...(nativeProps as ButtonHTMLAttributes<HTMLButtonElement>)}
          ref={trigger}
          id={selectID}
          type="button"
          role="combobox"
          aria-autocomplete="none"
          aria-controls={listboxID}
          aria-expanded={open}
          aria-haspopup="listbox"
          aria-label={
            props["aria-label"] ??
            (props["aria-labelledby"] ? undefined : placeholder)
          }
          aria-required={required || undefined}
          autoFocus={autoFocus}
          className="ark-control ark-select ark-select-trigger"
          data-autofocus={autoFocus || undefined}
          data-multiple={multiple || undefined}
          data-placeholder={selectedValues.length === 0 || undefined}
          data-size={size}
          disabled={disabled}
          form={form}
          tabIndex={tabIndex}
          title={title}
          onClick={(event) => {
            nativeProps.onClick?.(
              event as unknown as React.MouseEvent<HTMLSelectElement>,
            );
            if (event.defaultPrevented) return;
            if (listbox.current?.matches(":popover-open")) hideListbox(false);
            else showListbox();
          }}
          onKeyDown={(event) => {
            nativeProps.onKeyDown?.(
              event as unknown as React.KeyboardEvent<HTMLSelectElement>,
            );
            if (event.defaultPrevented) return;
            if (event.key === "ArrowDown") {
              event.preventDefault();
              showListbox("first");
            } else if (event.key === "ArrowUp") {
              event.preventDefault();
              showListbox("last");
            } else if (event.key === "Home") {
              event.preventDefault();
              showListbox("first");
            } else if (event.key === "End") {
              event.preventDefault();
              showListbox("last");
            } else if (
              event.key === "Escape" &&
              listbox.current?.matches(":popover-open")
            ) {
              event.preventDefault();
              hideListbox(true);
            }
          }}
        >
          <span className="ark-select-value">
            {multiple
              ? selectedLabels.length > 0
                ? selectedLabels.map(nodeText).join("、")
                : placeholder
              : (selectedEntry?.option.label ?? placeholder)}
          </span>
          <ChevronDown aria-hidden="true" size={16} strokeWidth={1.8} />
        </button>
        {required ? (
          <select
            aria-hidden="true"
            className="ark-select-validation"
            disabled={disabled}
            form={form}
            required
            tabIndex={-1}
            value={validationToken === undefined ? "" : String(validationToken)}
            onChange={() => undefined}
            onInvalid={(event) => {
              event.preventDefault();
              trigger.current?.focus();
            }}
          >
            <option value="" />
            {indexed.map(({ index }) => (
              <option key={index} value={String(index)} />
            ))}
          </select>
        ) : null}
        {clearable ? (
          <button
            type="button"
            className="ark-control ark-input-action"
            aria-label="清空选择"
            onClick={() => {
              if (disabled) return;
              if (props.mode === "multiple") {
                selectedValuesRef.current = [];
                if (!controlled) setLocalValue([]);
                props.onChange?.([]);
              } else if (props.allowClear) {
                if (!controlled) setLocalValue(undefined);
                props.onChange?.(undefined);
              }
              onClear?.();
            }}
          >
            <CloseSymbol />
          </button>
        ) : null}
      </span>
      <div
        ref={listbox}
        id={listboxID}
        popover="auto"
        role="listbox"
        aria-label={
          props["aria-label"] ??
          (props["aria-labelledby"] ? undefined : placeholder)
        }
        aria-multiselectable={multiple || undefined}
        className="ark-control ark-select-popover"
        onToggle={(event) => {
          const next = event.newState === "open";
          setOpen(next);
          if (!next) return;
          positionListbox();
          requestAnimationFrame(() => {
            positionListbox();
            const buttons = optionButtons();
            const selectedButton = buttons.find(
              (button) => button.dataset.selected === "true",
            );
            if (focusTarget.current === "none") {
              focusTarget.current = "selected";
              return;
            }
            const target =
              focusTarget.current === "first"
                ? buttons[0]
                : focusTarget.current === "last"
                  ? buttons.at(-1)
                  : (selectedButton ?? buttons[0]);
            target?.focus();
            focusTarget.current = "selected";
          });
        }}
        onKeyDown={(event) => {
          const buttons = optionButtons();
          const index = buttons.findIndex(
            (button) => button === document.activeElement,
          );
          let next: number | undefined;
          if (event.key === "ArrowDown")
            next = Math.min(index + 1, buttons.length - 1);
          else if (event.key === "ArrowUp") next = Math.max(index - 1, 0);
          else if (event.key === "Home") next = 0;
          else if (event.key === "End") next = buttons.length - 1;
          else if (event.key === "Escape") {
            event.preventDefault();
            event.stopPropagation();
            hideListbox(true);
          }
          if (next !== undefined) {
            event.preventDefault();
            buttons[next]?.focus();
          }
        }}
      >
        {visible.length > 0 ? (
          visible.map(({ option }) => {
            const optionSelected = selectedValues.includes(option.value);
            return (
              <button
                key={`${typeof option.value}:${String(option.value)}`}
                type="button"
                role="option"
                aria-selected={optionSelected}
                className="ark-select-option"
                data-selected={optionSelected || undefined}
                disabled={option.disabled}
                tabIndex={-1}
                onClick={() => choose(option)}
              >
                <span>{option.label}</span>
                {optionSelected ? (
                  <Check aria-hidden="true" size={15} strokeWidth={2} />
                ) : null}
              </button>
            );
          })
        ) : (
          <span className="ark-select-popover-empty">无匹配选项</span>
        )}
      </div>
      {name
        ? selectedValues.map((item) => (
            <input
              key={`${typeof item}:${item}`}
              type="hidden"
              name={name}
              value={String(item)}
              disabled={disabled}
              form={props.form}
            />
          ))
        : null}
      {showSearch && visible.length === 0 ? (
        <LiveRegion className="ark-select-empty">无匹配选项</LiveRegion>
      ) : null}
    </span>
  );
}

export interface SwitchProps extends Omit<
  ButtonHTMLAttributes<HTMLButtonElement>,
  "onChange"
> {
  checked?: boolean | undefined;
  defaultChecked?: boolean | undefined;
  onChange?: (checked: boolean) => void;
  loading?: boolean;
  size?: ControlSize;
  checkedChildren?: ReactNode;
  unCheckedChildren?: ReactNode;
  required?: boolean;
}

export const Switch = forwardRef<HTMLButtonElement, SwitchProps>(
  function Switch(
    {
      checked,
      defaultChecked = false,
      onChange,
      loading = false,
      disabled,
      size = "middle",
      checkedChildren,
      unCheckedChildren,
      className,
      form,
      name,
      onClick,
      required,
      value = "on",
      ...props
    },
    ref,
  ) {
    const [localChecked, setLocalChecked] = useState(defaultChecked);
    const buttonRef = useRef<HTMLButtonElement>(null);
    const inputRef = useRef<HTMLInputElement>(null);
    const controlled = checked !== undefined;
    const current = checked ?? localChecked;
    useEffect(() => {
      if (controlled) return;
      const formElement = inputRef.current?.form;
      if (!formElement) return;
      const reset = () => setLocalChecked(defaultChecked);
      formElement.addEventListener("reset", reset);
      return () => formElement.removeEventListener("reset", reset);
    }, [controlled, defaultChecked, form]);
    return (
      <>
        <button
          {...props}
          ref={(element) => {
            buttonRef.current = element;
            assignRef(ref, element);
          }}
          type="button"
          role="switch"
          aria-checked={current}
          aria-busy={loading || undefined}
          aria-required={required || undefined}
          disabled={disabled || loading}
          form={form}
          className={classes("ark-control ark-switch", className)}
          data-size={size}
          onClick={(event) => {
            if (disabled || loading) return;
            onClick?.(event);
            if (event.defaultPrevented) return;
            setLocalChecked(!current);
            onChange?.(!current);
          }}
        >
          <span className="ark-switch-thumb" />
          <span className="ark-switch-label">
            {current ? checkedChildren : unCheckedChildren}
          </span>
        </button>
        {name || required ? (
          <input
            ref={inputRef}
            aria-hidden="true"
            checked={current}
            className="ark-switch-validation"
            disabled={disabled || loading}
            form={form}
            name={name}
            onChange={() => undefined}
            onInvalid={(event) => {
              event.preventDefault();
              buttonRef.current?.focus();
            }}
            required={required}
            tabIndex={-1}
            type="checkbox"
            value={value}
          />
        ) : null}
      </>
    );
  },
);

interface ChoiceControlProps extends Omit<
  InputHTMLAttributes<HTMLInputElement>,
  "size" | "type"
> {
  size?: ControlSize;
  loading?: boolean;
  rootClassName?: string;
}

function ChoiceControl({
  type,
  size = "middle",
  loading = false,
  disabled,
  children,
  className,
  rootClassName,
  onChange,
  onClick,
  inputRef,
  indicator,
  mixed = false,
  ...props
}: ChoiceControlProps & {
  type: "checkbox" | "radio";
  inputRef: Ref<HTMLInputElement>;
  indicator: ReactNode;
  mixed?: boolean;
}) {
  const control = (
    <span
      className={classes(
        "ark-choice-control",
        children === undefined || children === null ? rootClassName : undefined,
      )}
      data-size={size}
      data-disabled={disabled || loading || undefined}
      data-loading={loading || undefined}
    >
      <input
        {...props}
        ref={inputRef}
        type={type}
        disabled={disabled || loading}
        aria-busy={loading || props["aria-busy"] || undefined}
        aria-checked={mixed ? "mixed" : props["aria-checked"]}
        className={classes("ark-choice-input", className)}
        onChange={(event) => {
          if (!disabled && !loading) onChange?.(event);
        }}
        onClick={(event) => {
          if (disabled || loading) {
            event.preventDefault();
            event.stopPropagation();
            return;
          }
          onClick?.(event);
        }}
      />
      <span aria-hidden="true" className="ark-choice-indicator">
        {loading ? <Spinner size={12} /> : indicator}
      </span>
    </span>
  );

  if (children === undefined || children === null) return control;
  return (
    <label
      className={classes("ark-control ark-choice", rootClassName)}
      data-disabled={disabled || loading || undefined}
    >
      {control}
      <span className="ark-choice-label">{children}</span>
    </label>
  );
}

export interface CheckboxProps extends ChoiceControlProps {
  indeterminate?: boolean;
}

export const Checkbox = /* @__PURE__ */ forwardRef<
  HTMLInputElement,
  CheckboxProps
>(function Checkbox({ indeterminate = false, ...props }, ref) {
  const input = useRef<HTMLInputElement>(null);
  useLayoutEffect(() => {
    if (input.current) input.current.indeterminate = indeterminate;
  }, [indeterminate]);

  return (
    <ChoiceControl
      {...props}
      type="checkbox"
      mixed={indeterminate}
      inputRef={(element) => {
        input.current = element;
        assignRef(ref, element);
      }}
      indicator={<Check />}
    />
  );
});

export type RadioProps = ChoiceControlProps;

export const Radio = /* @__PURE__ */ forwardRef<HTMLInputElement, RadioProps>(
  function Radio(props, ref) {
    return (
      <ChoiceControl
        {...props}
        type="radio"
        inputRef={ref}
        indicator={<span className="ark-radio-dot" />}
      />
    );
  },
);

const fieldControlTypes = new Set<unknown>([
  InputControl,
  TextArea,
  Password,
  FileInput,
  Select,
  Switch,
  Checkbox,
  Radio,
]);

interface FieldControlProps {
  id?: string;
  "aria-describedby"?: string;
  "aria-invalid"?: AriaAttributes["aria-invalid"];
}

export interface FormFieldProps {
  id: string;
  label: ReactNode;
  hint?: ReactNode;
  error?: ReactNode;
  children: ReactNode;
  className?: string;
}

export function FormField({
  id,
  label,
  hint,
  error,
  children,
  className,
}: FormFieldProps) {
  const hintID = `${id}-hint`;
  const errorID = `${id}-error`;
  const hasHint = hint !== undefined && hint !== null && hint !== false;
  const hasError = error !== undefined && error !== null && error !== false;
  const nodes = Children.toArray(children);
  const candidates = nodes.flatMap((node, index) => {
    if (!isValidElement<FieldControlProps>(node)) return [];
    const nativeControl =
      typeof node.type === "string" &&
      ["input", "select", "textarea"].includes(node.type);
    return nativeControl ||
      fieldControlTypes.has(node.type) ||
      node.props.id === id
      ? [index]
      : [];
  });
  const controlIndex = candidates.length === 1 ? candidates[0]! : -1;
  const control =
    controlIndex >= 0
      ? (nodes[controlIndex] as ReactElement<FieldControlProps>)
      : undefined;
  const controlID = control?.props.id ?? id;
  const descriptions = [
    ...(control?.props["aria-describedby"]?.split(/\s+/).filter(Boolean) ?? []),
    ...(hasHint ? [hintID] : []),
    ...(hasError ? [errorID] : []),
  ];
  const describedBy = [...new Set(descriptions)].join(" ") || undefined;
  const invalid = hasError ? true : control?.props["aria-invalid"];
  const content = control
    ? nodes.map((node, index) =>
        index === controlIndex
          ? cloneElement(control, {
              id: controlID,
              ...(describedBy ? { "aria-describedby": describedBy } : {}),
              ...(invalid !== undefined ? { "aria-invalid": invalid } : {}),
            })
          : node,
      )
    : children;

  return (
    <div className={classes("ark-control ark-form-field", className)}>
      <label className="ark-form-field-label" htmlFor={controlID}>
        {label}
      </label>
      {content}
      {hasHint ? (
        <small className="ark-form-field-hint" id={hintID}>
          {hint}
        </small>
      ) : null}
      {hasError ? (
        <InlineFeedback
          className="ark-form-field-error"
          id={errorID}
          type="error"
        >
          {error}
        </InlineFeedback>
      ) : null}
    </div>
  );
}

export type TableProps = ComponentPropsWithoutRef<"table">;
export type TableCaptionProps = ComponentPropsWithoutRef<"caption">;
export type TableColumnGroupProps = ComponentPropsWithoutRef<"colgroup">;
export type TableColumnProps = ComponentPropsWithoutRef<"col">;
export type TableSectionProps = ComponentPropsWithoutRef<"thead">;
export type TableRowProps = ComponentPropsWithoutRef<"tr">;
export type TableHeaderCellProps = ComponentPropsWithoutRef<"th">;
export type TableCellProps = ComponentPropsWithoutRef<"td">;

const TableControl = forwardRef<HTMLTableElement, TableProps>(
  function TableControl({ className, ...props }, ref) {
    return (
      <table
        {...props}
        ref={ref}
        className={classes("ark-table", className)}
      />
    );
  },
);

const TableCaption = forwardRef<
  HTMLTableCaptionElement,
  TableCaptionProps
>(function TableCaption({ className, ...props }, ref) {
  return (
    <caption
      {...props}
      ref={ref}
      className={classes("ark-table-caption", className)}
    />
  );
});

const TableColumnGroup = forwardRef<
  HTMLTableColElement,
  TableColumnGroupProps
>(function TableColumnGroup({ className, ...props }, ref) {
  return (
    <colgroup
      {...props}
      ref={ref}
      className={classes("ark-table-column-group", className)}
    />
  );
});

const TableColumn = forwardRef<HTMLTableColElement, TableColumnProps>(
  function TableColumn({ className, ...props }, ref) {
    return (
      <col
        {...props}
        ref={ref}
        className={classes("ark-table-column", className)}
      />
    );
  },
);

const TableHeader = forwardRef<HTMLTableSectionElement, TableSectionProps>(
  function TableHeader({ className, ...props }, ref) {
    return (
      <thead
        {...props}
        ref={ref}
        className={classes("ark-table-header", className)}
      />
    );
  },
);

const TableBody = forwardRef<HTMLTableSectionElement, TableSectionProps>(
  function TableBody({ className, ...props }, ref) {
    return (
      <tbody
        {...props}
        ref={ref}
        className={classes("ark-table-body", className)}
      />
    );
  },
);

const TableFooter = forwardRef<HTMLTableSectionElement, TableSectionProps>(
  function TableFooter({ className, ...props }, ref) {
    return (
      <tfoot
        {...props}
        ref={ref}
        className={classes("ark-table-footer", className)}
      />
    );
  },
);

const TableRow = forwardRef<HTMLTableRowElement, TableRowProps>(
  function TableRow({ className, ...props }, ref) {
    return (
      <tr
        {...props}
        ref={ref}
        className={classes("ark-table-row", className)}
      />
    );
  },
);

const TableHeaderCell = forwardRef<
  HTMLTableCellElement,
  TableHeaderCellProps
>(function TableHeaderCell({ className, ...props }, ref) {
  return (
    <th
      {...props}
      ref={ref}
      className={classes("ark-table-header-cell", className)}
    />
  );
});

const TableCell = forwardRef<HTMLTableCellElement, TableCellProps>(
  function TableCell({ className, ...props }, ref) {
    return (
      <td
        {...props}
        ref={ref}
        className={classes("ark-table-cell", className)}
      />
    );
  },
);

// eslint-disable-next-line react-refresh/only-export-components -- Native table parts share one public component API.
export const Table = Object.assign(TableControl, {
  Body: TableBody,
  Caption: TableCaption,
  Cell: TableCell,
  Column: TableColumn,
  ColumnGroup: TableColumnGroup,
  Footer: TableFooter,
  Header: TableHeader,
  HeaderCell: TableHeaderCell,
  Row: TableRow,
});

export interface TableViewportProps extends Omit<
  HTMLAttributes<HTMLDivElement>,
  "aria-busy" | "aria-label" | "role"
> {
  label: string;
  busy?: boolean | undefined;
}

export const TableViewport = forwardRef<HTMLDivElement, TableViewportProps>(
  function TableViewport(
    { label, busy = false, className, tabIndex = 0, children, ...props },
    ref,
  ) {
    return (
      <div
        {...props}
        ref={ref}
        role="region"
        aria-label={label}
        aria-busy={busy || undefined}
        className={classes("ark-control ark-table-viewport", className)}
        tabIndex={tabIndex}
      >
        {children}
      </div>
    );
  },
);

export interface TagProps extends HTMLAttributes<HTMLSpanElement> {
  color?: string;
  closable?: boolean;
  onClose?: () => void;
}

function semanticColor(color: string) {
  const named: Record<string, string> = {
    default: "var(--ark-text-secondary)",
    blue: "var(--ark-primary)",
    processing: "var(--ark-primary)",
    success: "var(--ark-success)",
    green: "var(--ark-success)",
    warning: "var(--ark-warning)",
    orange: "var(--ark-warning)",
    gold: "var(--ark-warning)",
    error: "var(--ark-danger)",
    red: "var(--ark-danger)",
  };
  return named[color] ?? color;
}

export function Tag({
  color = "default",
  closable = false,
  onClose,
  children,
  className,
  style,
  ...props
}: TagProps) {
  return (
    <span
      {...props}
      className={classes("ark-control ark-tag", className)}
      style={
        { "--ark-tag-color": semanticColor(color), ...style } as CSSProperties
      }
    >
      {children}
      {closable ? (
        <button
          type="button"
          aria-label="移除标签"
          className="ark-control ark-input-action"
          onClick={onClose}
        >
          <CloseSymbol />
        </button>
      ) : null}
    </span>
  );
}

export interface NoticeProps extends Omit<
  HTMLAttributes<HTMLDivElement>,
  "title"
> {
  type?: "info" | "success" | "warning" | "error";
  title?: ReactNode;
  description?: ReactNode;
  action?: ReactNode;
}

export function Notice({
  type = "info",
  title,
  description,
  action,
  children,
  className,
  ...props
}: NoticeProps) {
  return (
    <div
      role={type === "error" ? "alert" : "status"}
      {...props}
      className={classes("ark-control ark-notice", className)}
      data-type={type}
    >
      <div className="ark-notice-copy">
        {title ? <strong>{title}</strong> : null}
        {description ? <div>{description}</div> : null}
        {children}
      </div>
      {action ? <div className="ark-notice-action">{action}</div> : null}
    </div>
  );
}

export const Alert = Notice;

export interface EmptyStateProps extends HTMLAttributes<HTMLDivElement> {
  description?: ReactNode;
  action?: ReactNode;
}

export function EmptyState({
  description = "暂无内容",
  action,
  children,
  className,
  ...props
}: EmptyStateProps) {
  return (
    <div
      {...props}
      className={classes("ark-control ark-empty-state", className)}
    >
      <div className="ark-empty-state-description">{description}</div>
      {children}
      {action}
    </div>
  );
}

export interface TooltipProps {
  title: ReactNode;
  children: ReactElement<HTMLAttributes<HTMLElement>>;
  placement?: "top" | "bottom" | "left" | "right";
}

export function Tooltip({ title, children, placement = "top" }: TooltipProps) {
  const id = useId();
  const anchor = useRef<HTMLSpanElement>(null);
  const tooltip = useRef<HTMLSpanElement>(null);
  const [hovered, setHovered] = useState(false);
  const [focused, setFocused] = useState(false);
  const [dismissed, setDismissed] = useState(false);
  const hoveredRef = useRef(false);
  const focusedRef = useRef(false);
  const dismissedRef = useRef(false);
  const visible = Boolean(title) && (hovered || focused) && !dismissed;
  const position = useCallback(() => {
    const target = anchor.current;
    const surface = tooltip.current;
    if (!target || !surface || !surface.matches(":popover-open")) return;
    const margin = 8;
    const gap = 6;
    const anchorRect = target.getBoundingClientRect();
    const width = surface.offsetWidth;
    const height = surface.offsetHeight;
    const viewportWidth = document.documentElement.clientWidth;
    const viewportHeight = document.documentElement.clientHeight;
    surface.style.bottom = "auto";
    surface.style.right = "auto";
    surface.style.transform = "none";
    let left = anchorRect.left + (anchorRect.width - width) / 2;
    let top = anchorRect.top - height - gap;
    if (placement === "bottom") top = anchorRect.bottom + gap;
    else if (placement === "left") {
      left = anchorRect.left - width - gap;
      top = anchorRect.top + (anchorRect.height - height) / 2;
    } else if (placement === "right") {
      left = anchorRect.right + gap;
      top = anchorRect.top + (anchorRect.height - height) / 2;
    }
    if (placement === "top" && top < margin) top = anchorRect.bottom + gap;
    if (placement === "bottom" && top + height > viewportHeight - margin)
      top = anchorRect.top - height - gap;
    if (placement === "left" && left < margin) left = anchorRect.right + gap;
    if (placement === "right" && left + width > viewportWidth - margin)
      left = anchorRect.left - width - gap;
    surface.style.left = `${Math.max(
      margin,
      Math.min(left, viewportWidth - width - margin),
    )}px`;
    surface.style.top = `${Math.max(
      margin,
      Math.min(top, viewportHeight - height - margin),
    )}px`;
  }, [placement]);

  useLayoutEffect(() => {
    const surface = tooltip.current;
    if (!surface) return;
    const shouldShow = visible && !dismissedRef.current;
    surface.hidden = !shouldShow;
    if (!shouldShow) {
      if (surface.matches(":popover-open")) surface.hidePopover();
      return;
    }
    if (!surface.matches(":popover-open")) surface.showPopover();
    position();
    const frame = requestAnimationFrame(position);
    return () => cancelAnimationFrame(frame);
  }, [position, title, visible]);

  useEffect(() => {
    if (!visible) return;
    window.addEventListener("resize", position);
    window.addEventListener("scroll", position, true);
    return () => {
      window.removeEventListener("resize", position);
      window.removeEventListener("scroll", position, true);
    };
  }, [position, visible]);

  return (
    <span
      ref={anchor}
      className="ark-tooltip-anchor"
      onMouseEnter={() => {
        hoveredRef.current = true;
        setHovered(true);
      }}
      onMouseLeave={() => {
        hoveredRef.current = false;
        setHovered(false);
        if (!focusedRef.current) {
          dismissedRef.current = false;
          setDismissed(false);
        }
      }}
      onFocus={() => {
        focusedRef.current = true;
        setFocused(true);
      }}
      onBlur={(event) => {
        if (!event.currentTarget.contains(event.relatedTarget)) {
          focusedRef.current = false;
          setFocused(false);
          if (!hoveredRef.current) {
            dismissedRef.current = false;
            setDismissed(false);
          }
        }
      }}
      onKeyDown={(event) => {
        const surface = tooltip.current;
        if (
          event.key === "Escape" &&
          (visible || surface?.matches(":popover-open"))
        ) {
          dismissedRef.current = true;
          if (surface) surface.hidden = true;
          if (surface?.matches(":popover-open")) surface.hidePopover();
          setDismissed(true);
          event.stopPropagation();
        }
      }}
    >
      {cloneElement(children, {
        "aria-describedby":
          classes(children.props["aria-describedby"], visible && id) ||
          undefined,
      })}
      <span
        ref={tooltip}
        popover="manual"
        role="tooltip"
        id={id}
        className="ark-control ark-tooltip"
        data-placement={placement}
      >
        {title}
      </span>
    </span>
  );
}

export interface DialogProps extends Omit<
  HTMLAttributes<HTMLDialogElement>,
  "title" | "onClose"
> {
  open: boolean;
  onClose: () => void;
  title?: ReactNode;
  footer?: ReactNode;
  children?: ReactNode;
  width?: CSSProperties["width"];
  closeOnBackdrop?: boolean;
}

const modalStack: HTMLDialogElement[] = [];
const modalRestoreTargets = new WeakMap<HTMLDialogElement, HTMLElement[]>();
const focusSelector =
  'button, [href], input, select, textarea, [tabindex], [contenteditable="true"]';

function focusableElements(root: HTMLElement) {
  return Array.from(root.querySelectorAll<HTMLElement>(focusSelector)).filter(
    (element) =>
      element.tabIndex >= 0 &&
      !element.matches(":disabled") &&
      !element.closest("[hidden], [inert]") &&
      element.getClientRects().length > 0 &&
      getComputedStyle(element).visibility !== "hidden",
  );
}

export function Dialog({
  open,
  onClose,
  title,
  footer,
  children,
  className,
  style,
  width = 560,
  closeOnBackdrop = true,
  ...props
}: DialogProps) {
  const dialog = useRef<HTMLDialogElement>(null);
  const close = useRef(onClose);
  const titleID = useId();
  useLayoutEffect(() => {
    close.current = onClose;
  }, [onClose]);
  useEffect(() => {
    const element = dialog.current;
    if (!open || !element) return;
    const previousFocus =
      document.activeElement instanceof HTMLElement
        ? document.activeElement
        : null;
    const parent = modalStack[modalStack.length - 1];
    const restoreTargets = [
      ...(previousFocus ? [previousFocus] : []),
      ...(parent ? (modalRestoreTargets.get(parent) ?? []) : []),
    ];
    modalRestoreTargets.set(element, restoreTargets);
    if (!element.open) element.showModal();
    modalStack.push(element);
    const isTop = () => modalStack[modalStack.length - 1] === element;
    const focusInside = () =>
      (focusableElements(element)[0] ?? element).focus();
    const preferredFocus = element.querySelector<HTMLElement>(
      "[autofocus], [data-autofocus='true']",
    );
    if (preferredFocus && focusableElements(element).includes(preferredFocus))
      preferredFocus.focus();
    else if (!element.contains(document.activeElement)) focusInside();
    function keydown(event: KeyboardEvent) {
      if (!isTop()) return;
      if (event.key === "Escape" && !event.defaultPrevented) {
        event.preventDefault();
        event.stopPropagation();
        close.current();
      } else if (event.key === "Tab" && !event.defaultPrevented) {
        const targets = focusableElements(element!);
        const first = targets[0];
        const last = targets[targets.length - 1];
        if (!first || !last) {
          event.preventDefault();
          element!.focus();
        } else if (
          event.shiftKey &&
          (document.activeElement === first ||
            document.activeElement === element)
        ) {
          event.preventDefault();
          last.focus();
        } else if (
          !event.shiftKey &&
          (document.activeElement === last ||
            document.activeElement === element)
        ) {
          event.preventDefault();
          first.focus();
        }
      }
    }
    function focusin(event: FocusEvent) {
      if (
        isTop() &&
        event.target instanceof Node &&
        !element!.contains(event.target)
      )
        focusInside();
    }
    document.addEventListener("keydown", keydown);
    document.addEventListener("focusin", focusin);
    return () => {
      document.removeEventListener("keydown", keydown);
      document.removeEventListener("focusin", focusin);
      const index = modalStack.indexOf(element);
      const wasTop = isTop();
      if (index >= 0) modalStack.splice(index, 1);
      if (element.open) element.close();
      if (wasTop) {
        const remainingDialog = modalStack[modalStack.length - 1];
        const target = restoreTargets.find(
          (candidate) =>
            candidate.isConnected &&
            !candidate.matches(":disabled") &&
            !candidate.closest("[inert]") &&
            (!remainingDialog || remainingDialog.contains(candidate)),
        );
        (target ?? remainingDialog)?.focus();
      }
    };
  }, [open]);
  if (!open || typeof document === "undefined") return null;
  return createPortal(
    <dialog
      {...props}
      ref={dialog}
      tabIndex={-1}
      aria-modal="true"
      aria-labelledby={title ? titleID : props["aria-labelledby"]}
      aria-label={props["aria-label"] ?? (title ? undefined : "对话框")}
      className={classes("ark-control ark-dialog", className)}
      style={{ width, ...style }}
      onCancel={(event) => {
        event.preventDefault();
        event.stopPropagation();
        close.current();
      }}
      onClick={(event) => {
        props.onClick?.(event);
        if (
          !closeOnBackdrop ||
          event.defaultPrevented ||
          event.target !== event.currentTarget
        )
          return;
        const rect = event.currentTarget.getBoundingClientRect();
        if (
          event.clientX < rect.left ||
          event.clientX > rect.right ||
          event.clientY < rect.top ||
          event.clientY > rect.bottom
        )
          close.current();
      }}
    >
      <header className="ark-dialog-header">
        {title ? (
          <h2 id={titleID} className="ark-dialog-title">
            {title}
          </h2>
        ) : null}
        <Button
          className="ark-dialog-close"
          type="text"
          aria-label="关闭对话框"
          icon={<CloseSymbol />}
          onClick={onClose}
        />
      </header>
      <div className="ark-dialog-body">{children}</div>
      {footer ? <footer className="ark-dialog-footer">{footer}</footer> : null}
    </dialog>,
    document.body,
  );
}

export interface TabItem {
  key: string;
  label: ReactNode;
  children: ReactNode;
  disabled?: boolean;
}

export interface TabsProps extends Omit<
  HTMLAttributes<HTMLDivElement>,
  "onChange"
> {
  items: ReadonlyArray<TabItem>;
  activeKey?: string;
  defaultActiveKey?: string;
  onChange?: (key: string) => void;
}

export function Tabs({
  items,
  activeKey,
  defaultActiveKey,
  onChange,
  className,
  ...props
}: TabsProps) {
  const id = useId();
  const root = useRef<HTMLDivElement>(null);
  const tabList = useRef<HTMLDivElement>(null);
  const previousKey = useRef<string | undefined>(undefined);
  const previousIndex = useRef(-1);
  const policy = useMotionPolicy();
  const [localKey, setLocalKey] = useState(defaultActiveKey);
  const selectedKey = activeKey ?? localKey;
  const current =
    items.find((item) => item.key === selectedKey && !item.disabled) ??
    items.find((item) => !item.disabled);
  const currentIndex = current ? items.indexOf(current) : -1;
  useLayoutEffect(() => {
    const previous = previousKey.current;
    const fromIndex = previousIndex.current;
    previousKey.current = current?.key;
    previousIndex.current = currentIndex;
    if (previous === undefined || previous === current?.key || !policy.enabled)
      return;
    const panel = root.current?.querySelector<HTMLElement>(
      ':scope > [role="tabpanel"]:not([hidden])',
    );
    if (!panel?.animate) return;
    const direction = currentIndex > fromIndex ? 1 : -1;
    const animation = panel.animate(
      [
        {
          opacity: 0.35,
          transform: `translateX(${direction * policy.distance}px)`,
        },
        { opacity: 1, transform: "translateX(0)" },
      ],
      { duration: policy.duration, easing: policy.easing },
    );
    return () => animation.cancel();
  }, [
    current?.key,
    currentIndex,
    policy.enabled,
    policy.duration,
    policy.distance,
    policy.easing,
  ]);
  function activate(item: TabItem) {
    if (item.disabled || item.key === current?.key) return;
    setLocalKey(item.key);
    onChange?.(item.key);
  }
  return (
    <div
      {...props}
      ref={root}
      className={classes("ark-control ark-tabs", className)}
    >
      <div
        ref={tabList}
        role="tablist"
        aria-label={props["aria-label"] ?? "选项卡"}
        className="ark-tab-list"
      >
        <SelectionIndicator rootRef={tabList} variant="underline" />
        {items.map((item, index) => (
          <button
            type="button"
            role="tab"
            key={item.key}
            id={`${id}-tab-${index}`}
            aria-controls={`${id}-panel-${index}`}
            aria-selected={item.key === current?.key}
            data-ark-selection-item=""
            disabled={item.disabled}
            tabIndex={item.key === current?.key ? 0 : -1}
            className="ark-control ark-tab"
            onClick={() => activate(item)}
            onKeyDown={(event) => {
              if (
                !["ArrowLeft", "ArrowRight", "Home", "End"].includes(event.key)
              )
                return;
              event.preventDefault();
              const enabled = items.filter((candidate) => !candidate.disabled);
              const position = enabled.indexOf(item);
              const next =
                enabled[
                  event.key === "Home"
                    ? 0
                    : event.key === "End"
                      ? enabled.length - 1
                      : (position +
                          (event.key === "ArrowRight" ? 1 : -1) +
                          enabled.length) %
                        enabled.length
                ];
              if (next) {
                activate(next);
                document
                  .getElementById(`${id}-tab-${items.indexOf(next)}`)
                  ?.focus();
              }
            }}
          >
            {item.label}
          </button>
        ))}
      </div>
      {items.map((item, index) => (
        <div
          role="tabpanel"
          key={item.key}
          id={`${id}-panel-${index}`}
          aria-labelledby={`${id}-tab-${index}`}
          tabIndex={0}
          hidden={item.key !== current?.key}
          className="ark-tab-panel"
        >
          {item.children}
        </div>
      ))}
    </div>
  );
}

export interface CardProps extends Omit<HTMLAttributes<HTMLElement>, "title"> {
  title?: ReactNode;
  extra?: ReactNode;
}

export function Card({
  title,
  extra,
  className,
  children,
  ...props
}: CardProps) {
  return (
    <section {...props} className={classes("ark-control ark-card", className)}>
      {title || extra ? (
        <header className="ark-card-header">
          {title ? <h3>{title}</h3> : null}
          {extra}
        </header>
      ) : null}
      <div className="ark-card-body">{children}</div>
    </section>
  );
}

export type DividerProps = ComponentPropsWithoutRef<"hr">;

export const Divider = forwardRef<HTMLHRElement, DividerProps>(
  function Divider({ className, ...props }, ref) {
    return (
      <hr
        {...props}
        ref={ref}
        className={classes("ark-control ark-divider", className)}
      />
    );
  },
);

export interface StackProps extends HTMLAttributes<HTMLDivElement> {
  gap?: CSSProperties["gap"];
  direction?: "horizontal" | "vertical" | "row" | "column";
  align?: "start" | "center" | "end" | "stretch" | "baseline";
  justify?: CSSProperties["justifyContent"];
  wrap?: boolean;
}

export function Stack({
  gap = 12,
  direction = "horizontal",
  align = "center",
  justify,
  wrap = false,
  style,
  className,
  ...props
}: StackProps) {
  return (
    <div
      {...props}
      className={classes("ark-control ark-stack", className)}
      style={{
        gap,
        flexDirection:
          direction === "vertical" || direction === "column" ? "column" : "row",
        alignItems:
          align === "start"
            ? "flex-start"
            : align === "end"
              ? "flex-end"
              : align,
        justifyContent: justify,
        flexWrap: wrap ? "wrap" : "nowrap",
        ...style,
      }}
    />
  );
}

type TextElement = "span" | "p" | "div";

interface TextOwnProps {
  as?: TextElement;
  type?:
    "secondary" | "tertiary" | "success" | "warning" | "danger" | "disabled";
  strong?: boolean;
  code?: boolean;
  clamp?: number;
}

export type TextProps<Element extends TextElement = "span"> = TextOwnProps &
  Omit<ComponentPropsWithoutRef<Element>, keyof TextOwnProps>;

export function Text<Element extends TextElement = "span">({
  as,
  type,
  strong = false,
  code = false,
  clamp,
  children,
  className,
  style,
  ...props
}: TextProps<Element>) {
  const Component = as ?? "span";
  const clampLines =
    clamp === undefined || !Number.isFinite(clamp)
      ? undefined
      : Math.max(1, Math.floor(clamp));
  const content = code ? <code>{children}</code> : children;
  return (
    <Component
      {...props}
      className={classes("ark-control ark-text", className)}
      data-clamp={clampLines}
      data-type={type}
      style={
        clampLines === undefined
          ? style
          : ({
              ...style,
              "--ark-text-clamp-lines": clampLines,
            } as CSSProperties)
      }
    >
      {strong ? <strong>{content}</strong> : content}
    </Component>
  );
}

export interface TitleProps extends HTMLAttributes<HTMLHeadingElement> {
  level?: 1 | 2 | 3 | 4 | 5;
}

export function Title({ level = 3, className, ...props }: TitleProps) {
  const Heading = `h${level}` as const;
  return (
    <Heading
      {...props}
      className={classes("ark-control ark-title", className)}
    />
  );
}

export { Avatar, CommandMenu, type CommandItem } from "./CommandMenu";
export { Image, type ImageProps } from "./Image";
